import { useCallback, useEffect, useState } from "react";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import {
  createBellNotification,
  deleteBellNotifications,
  getBellNotifications,
  updateBellNotification,
} from "sj-ui-toolkit/src/api";

import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";

import { NotificationsListCard } from "@/components/bell-notifications/NotificationsListCard";
import { EditNotificationDialog } from "@/components/bell-notifications/EditNotificationDialog";
import type {
  NotificationData,
  EditFormState,
  TableRow,
} from "@/components/bell-notifications/types";
import { SKELETON_ROWS } from "@/components/bell-notifications/types";

const DEFAULT_EDIT_FORM: EditFormState = { id: null, title: "", description: "" };

export function BellNotifications() {
  useBreadcrumb({
    label: "Bell Notifications",
    route: AdminAppRoutes.routes.comms.routes.notify.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();

  const [notifications, setNotifications] = useState<NotificationData[] | "loading">("loading");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<NotificationData[]>([]);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>(DEFAULT_EDIT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getBellNotifications();
      setNotifications(data ?? []);
    } catch (error) {
      console.error("Failed to load notifications:", error);
      setNotifications([]);
      showToast({ severity: "error", detail: "Failed to load notifications" });
    }
  }, [showToast]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handlePublish = async () => {
    if (!title || !description) {
      showToast({ severity: "warn", detail: "Please provide both a header and message" });
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await createBellNotification({ title, description });
      setTitle("");
      setDescription("");
      await fetchNotifications();
      showToast({ severity: "success", detail: "Notification published" });
    } catch (error) {
      console.error("Failed to publish notification:", error);
      showToast({ severity: "error", detail: "Failed to publish notification" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    const selectedCount = selected.length;
    setIsDeleting(true);
    try {
      await deleteBellNotifications({ ids: selected.map(n => n.id) });
      setSelected([]);
      await fetchNotifications();
      showToast({ severity: "success", detail: `${selectedCount} notification(s) deleted` });
    } catch (error) {
      console.error("Failed to delete notifications:", error);
      showToast({ severity: "error", detail: "Failed to delete notifications" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = useCallback((notification: NotificationData) => {
    setEditForm({
      id: notification.id,
      title: notification.title,
      description: notification.description,
    });
    setEditDialogVisible(true);
  }, []);

  const handleSaveEdit = async () => {
    if (!editForm.title || !editForm.description) {
      showToast({ severity: "warn", detail: "Please provide both a header and message" });
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await updateBellNotification({
        id: editForm.id!,
        title: editForm.title,
        description: editForm.description,
      });
      setEditDialogVisible(false);
      setEditForm(DEFAULT_EDIT_FORM);
      await fetchNotifications();
      showToast({ severity: "success", detail: "Notification updated" });
    } catch (error) {
      console.error("Failed to update notification:", error);
      showToast({ severity: "error", detail: "Failed to update notification" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = useCallback(() => {
    setEditDialogVisible(false);
    setEditForm(DEFAULT_EDIT_FORM);
  }, []);

  const tableRows: TableRow[] = notifications === "loading" ? SKELETON_ROWS : notifications;

  return (
    <div className="flex flex-col gap-12">
      <div className="sr-only">
        <h1>Bell Notifications</h1>
      </div>

      <Card title="Add a new bell notification">
        <div className="flex flex-col gap-4">
          <InputText placeholder="Header" value={title} onChange={e => setTitle(e.target.value)} />
          <InputTextarea
            placeholder="Message"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <Button
            className="w-fit self-end mt-8"
            label="Publish Notification"
            icon="pi pi-send"
            onClick={handlePublish}
            loading={isSubmitting}
          />
        </div>
      </Card>

      <NotificationsListCard
        rows={tableRows}
        selected={selected}
        onSelectionChange={setSelected}
        onDelete={handleDelete}
        onEdit={handleEdit}
        isDeleting={isDeleting}
      />

      <EditNotificationDialog
        visible={editDialogVisible}
        formData={editForm}
        onChange={setEditForm}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        isSaving={isSubmitting}
      />
    </div>
  );
}
